import { useMemo } from 'react';
import { Sun, Leaf, DollarSign, Loader2, ArrowUpFromLine, Unplug } from 'lucide-react';
import { useEnergyTimeSeries } from '@/hooks/useEnergyTimeSeries';
import type { CompareSlot, TimeGranularity, GroupTimeSeriesPoint } from '@/types/reports';
import type { Group, IndustryType } from '@/types';

interface CompareKPIsSectionProps {
  slot: CompareSlot;
  dateRange: { start: Date | null; end: Date | null } | null;
  granularity: TimeGranularity;
  groups: Group[];
  selectedIndustryTypes: IndustryType[];
}

export function CompareKPIsSection({
  slot,
  dateRange,
  granularity,
  groups: _groups,
  selectedIndustryTypes,
}: CompareKPIsSectionProps) {
  // Fetch time series data for this slot
  const { timeSeries: timeSeriesData, isLoading } = useEnergyTimeSeries({
    groupIds: slot.groupIds,
    startDate: dateRange?.start || null,
    endDate: dateRange?.end || null,
    granularity,
    modelCodes: slot.modelCodes,
    industryTypes: selectedIndustryTypes,
    enabled: slot.groupIds.length > 0 && !!dateRange,
  });

  // Calculate KPI values from time series data
  const kpis = useMemo(() => {
    if (!timeSeriesData || timeSeriesData.length === 0) {
      return {
        solarKwh: 0,
        offGridPercent: 0,
        co2Reduced: 0,
        costSavings: 0,
        gridExportKwh: 0,
        gridSellRevenue: 0,
      };
    }

    // Sum up values across all time periods
    const totals = timeSeriesData.reduce(
      (acc: { solarKwh: number; gridKwh: number; totalKwh: number; gridExportKwh: number; gridSellRevenue: number; co2Reduced: number; costSaved: number; generatorKwh: number }, point: GroupTimeSeriesPoint) => ({
        solarKwh: acc.solarKwh + (point.solarKwh || 0),
        gridKwh: acc.gridKwh + (point.gridKwh || 0),
        totalKwh: acc.totalKwh + (point.solarKwh || 0) + (point.gridKwh || 0),
        gridExportKwh: acc.gridExportKwh + (point.gridExportKwh || 0),
        gridSellRevenue: acc.gridSellRevenue + (point.gridSellRevenue || 0),
        co2Reduced: acc.co2Reduced + (point.co2Reduced || 0),
        costSaved: acc.costSaved + (point.costSaved || 0),
        generatorKwh: acc.generatorKwh + (point.generatorKwh || 0),
      }),
      { solarKwh: 0, gridKwh: 0, totalKwh: 0, gridExportKwh: 0, gridSellRevenue: 0, co2Reduced: 0, costSaved: 0, generatorKwh: 0 }
    );

    // Energy-weighted share from measured kWh totals, generator included
    // (the RPC's offGridPercent field is generator-blind). Matches the
    // charts panel's derivation.
    const totalEnergy = totals.solarKwh + totals.gridKwh + totals.generatorKwh;
    const offGridPercent = totalEnergy > 0
      ? (totals.solarKwh / totalEnergy) * 100
      : 0;

    // CO2, savings and revenue all come from the RPC (matches the Charts tab,
    // the dashboard and the Excel export). Savings price each unit-day at its
    // configured rate (audit F03); revenue is authoritative, 0 means 0.
    const co2Reduced = totals.co2Reduced;
    const costSavings = totals.costSaved;
    const gridSellRevenue = totals.gridSellRevenue;

    return {
      solarKwh: totals.solarKwh,
      offGridPercent,
      co2Reduced,
      costSavings,
      gridExportKwh: totals.gridExportKwh,
      gridSellRevenue,
    };
  }, [timeSeriesData]);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-full min-h-[150px]">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  // Format kWh to GWh if >= 1,000,000 (7+ digits), otherwise show 6 digits in kWh
  const formatEnergy = (kwh: number): { value: string; unit: string } => {
    if (kwh >= 1_000_000) {
      return {
        value: (kwh / 1_000_000).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 }),
        unit: 'GWh',
      };
    }
    return {
      value: Math.round(kwh).toLocaleString(),
      unit: 'kWh',
    };
  };

  // Always show CO2 in kg (user requested consistency)
  const formatWeight = (kg: number): { value: string; unit: string } => {
    return {
      value: Math.round(kg).toLocaleString(),
      unit: 'kg',
    };
  };

  const solarFormatted = formatEnergy(kpis.solarKwh);
  const gridExportFormatted = formatEnergy(kpis.gridExportKwh);
  const co2Formatted = formatWeight(kpis.co2Reduced);

  const kpiItems = [
    {
      label: 'Solar Generated',
      value: solarFormatted.value,
      unit: solarFormatted.unit,
      icon: Sun,
      color: slot.color,
    },
    {
      label: 'Off-Grid %',
      value: kpis.offGridPercent.toFixed(1),
      unit: '%',
      icon: Unplug,
      color: slot.color,
    },
    {
      label: 'CO₂ Reduced',
      value: co2Formatted.value,
      unit: co2Formatted.unit,
      icon: Leaf,
      color: slot.color,
    },
    {
      label: 'Est. Savings',
      value: `$${kpis.costSavings.toLocaleString(undefined, { maximumFractionDigits: 0 })}`,
      unit: '',
      icon: DollarSign,
      color: slot.color,
    },
    {
      label: 'Grid Export',
      value: gridExportFormatted.value,
      unit: gridExportFormatted.unit,
      icon: ArrowUpFromLine,
      color: slot.color,
    },
    {
      label: 'Grid Revenue',
      value: `$${kpis.gridSellRevenue.toLocaleString(undefined, { maximumFractionDigits: 0 })}`,
      unit: '',
      icon: DollarSign,
      color: slot.color,
    },
  ];

  return (
    <div className="grid grid-cols-2 gap-2 @container">
      {kpiItems.map((kpi) => (
        <div
          key={kpi.label}
          className="bg-muted/30 rounded-lg p-2 space-y-1"
        >
          <div className="flex items-center gap-1.5">
            <kpi.icon
              className="h-4 w-4 shrink-0"
              style={{ color: kpi.color }}
            />
            <span className="text-[10px] text-muted-foreground uppercase tracking-wider leading-tight">
              {kpi.label}
            </span>
          </div>
          <div className="text-base font-bold leading-tight text-right">
            <span className="break-all">{kpi.value}</span>
            {kpi.unit && (
              <span className="text-xs font-normal text-muted-foreground ml-0.5">
                {kpi.unit}
              </span>
            )}
          </div>
        </div>
      ))}
    </div>
  );
}
